import { useEffect, useRef, useState } from "react";
import { getProtectedImageBlob } from "services/cameraApi";

// Fetches each image's thumbnail as an authenticated blob (thumbnail_url
// requires JWT/session auth, so a plain <img src> can't load it directly)
// and caches it as an object URL keyed by image id, bounded to 6 concurrent
// fetches. Extracted from useCamera.js's own gallery-loading effect so the
// Experiment page's chart-hover markers can reuse the exact same
// fetch/cache/cleanup behavior instead of re-implementing it.
export function useThumbnailCache(images) {
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const thumbnailRef = useRef({});
  // Which thumbnails are wanted, as a string: the list is usually re-fetched as a NEW array with the
  // same photos in it (a poll, a label saved), and that must not redo the cache or re-render anything.
  const wanted = images.map((image) => `${image.id}|${image.thumbnail_url}`).join("\n");
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    const current = imagesRef.current;
    const controller = new AbortController();
    let active = true;
    const currentIds = new Set(current.map((image) => String(image.id)));
    let revoked = false;
    Object.entries(thumbnailRef.current).forEach(([id, url]) => {
      if (!currentIds.has(id)) {
        URL.revokeObjectURL(url);
        delete thumbnailRef.current[id];
        revoked = true;
      }
    });
    if (revoked) setThumbnailUrls({ ...thumbnailRef.current });

    const queue = current.filter((image) => !thumbnailRef.current[String(image.id)]);
    const worker = async () => {
      while (active && queue.length) {
        const image = queue.shift();
        try {
          const result = await getProtectedImageBlob(image.thumbnail_url, {
            signal: controller.signal,
          });
          if (!active) return;
          const url = URL.createObjectURL(result.blob);
          thumbnailRef.current[String(image.id)] = url;
          setThumbnailUrls({ ...thumbnailRef.current });
        } catch (requestError) {
          if (requestError.name !== "AbortError" && active) {
            // Best-effort cache: one failed thumbnail shouldn't stop the rest
            // from loading. Callers that need to surface fetch errors should
            // do so from their own image-list-loading path, not from here.
          }
        }
      }
    };
    Promise.all(Array.from({ length: Math.min(6, queue.length) }, worker));
    return () => {
      active = false;
      controller.abort();
    };
  }, [wanted]);

  useEffect(
    () => () => {
      Object.values(thumbnailRef.current).forEach((url) => URL.revokeObjectURL(url));
      thumbnailRef.current = {};
    },
    []
  );

  return thumbnailUrls;
}
